import { LimeCheckboxGroup } from "@/Components/NextBase/LimeCheckboxGroup";
import { t } from "@lingui/core/macro";
import { api } from "@/lib/api-client";
import { useAppointmentModalStore } from "../../useAppointmentModalStore_";
import { useEffect, useState } from "react";
import { CustomerSubscriptionItem } from "./CustomerSubscriptionItem";
import { Divider, Skeleton, Spinner } from "@heroui/react";
import { uniqBy } from "lodash";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { cn } from "@/utils";

export const CustomerSubscriptionsList = ({
  customerId,
  customerSubscriptionId,
  isNewAppointment,
  onCustomerSubscriptionChange,
  allowInitialAutoSelect = false,
  locationId,
}: {
  customerId: number;
  customerSubscriptionId: number | null;
  isNewAppointment: boolean;
  onCustomerSubscriptionChange: (customerSubscriptionId: number | null) => void;
  allowInitialAutoSelect?: boolean;
  locationId: number | null;
}) => {
  const {
    data: customerSubscriptionsResponse,
    isFetching: isCustomerSubscriptionsLoading,
    processedErrorMessage: customerSubscriptionsError,
  } = api.customer.useGetCustomerSubscriptionsForCustomer({
    customerId: customerId ?? -1,
    status: "all",
  });

  const allCustomerSubscriptions =
    customerSubscriptionsResponse?.customerSubscriptions ?? [];

  const { selectedServices } = useAppointmentModalStore((state) => ({
    selectedServices: state.selectedServices,
  }));

  const selectedServiceIds = selectedServices.map(
    (s: { serviceId: number }) => s.serviceId,
  );

  const activeOrFrozenSubscriptions = allCustomerSubscriptions.filter(
    (sub) => sub.status === "active" || sub.status === "frozen",
  );

  const [hasUserTouchedSelection, setHasUserTouchedSelection] = useState(false);

  useEffect(() => {
    if (!allowInitialAutoSelect) return;
    if (hasUserTouchedSelection) return;
    if (customerSubscriptionId) return;
    if (selectedServiceIds.length === 0) return;

    const match = activeOrFrozenSubscriptions.find((subscription) => {
      const isUsableHere =
        locationId == null ||
        subscription.allLocations ||
        subscription.locationIds.includes(locationId);
      if (!isUsableHere) return false;

      const subscriptionServiceIds =
        subscription.customerSubscriptionServices.map(
          (service) => service.serviceId,
        );
      return selectedServiceIds.every((serviceId: number) =>
        subscriptionServiceIds.includes(serviceId),
      );
    });

    if (!match) return;

    onCustomerSubscriptionChange(match.id);
  }, [
    activeOrFrozenSubscriptions,
    selectedServiceIds,
    allowInitialAutoSelect,
    hasUserTouchedSelection,
    customerSubscriptionId,
    locationId,
  ]);

  const selectedSubscriptionFromAll = customerSubscriptionId
    ? allCustomerSubscriptions.find(
        (sub) => sub.id === customerSubscriptionId,
      ) || null
    : null;

  const displaySubscriptions = (() => {
    if (!isNewAppointment && selectedSubscriptionFromAll) {
      return uniqBy(
        [selectedSubscriptionFromAll, ...activeOrFrozenSubscriptions],
        (s) => s.id,
      );
    }
    return activeOrFrozenSubscriptions;
  })();

  const selectedCustomerSubscriptionId = (() => {
    if (!customerSubscriptionId) return [];
    const isPresent = displaySubscriptions.some(
      (s) => s.id === customerSubscriptionId,
    );
    return isPresent ? [customerSubscriptionId.toString()] : [];
  })();

  return (
    <>
      <LimeAlert
        color="danger"
        message={customerSubscriptionsError}
        className="mb-4"
      />

      <Divider />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">
          {isNewAppointment ? t`Izberi paket` : t`Izbran paket`}
        </p>
        <LimeCheckboxGroup
          className="flex w-full flex-col gap-2"
          value={selectedCustomerSubscriptionId}
          onValueChange={(values) => {
            setHasUserTouchedSelection(true);
            const last = values[values.length - 1];
            onCustomerSubscriptionChange(
              values.length > 0 ? parseInt(last) : null,
            );
          }}
        >
          {isCustomerSubscriptionsLoading &&
            displaySubscriptions.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <Spinner />
              </div>
            )}
          {displaySubscriptions.map((customerSubscription) => {
            return (
              <Skeleton
                key={customerSubscription.id}
                isLoaded={!isCustomerSubscriptionsLoading}
                className={cn("rounded-xl", {
                  "mr-2": isCustomerSubscriptionsLoading,
                })}
              >
                <CustomerSubscriptionItem
                  // key={customerSubscription.id}
                  value={customerSubscription.id.toString()}
                  isSelected={
                    customerSubscription.id === customerSubscriptionId
                  }
                  customerSubscription={customerSubscription}
                  isNewAppointment={isNewAppointment}
                  locationId={locationId}
                />
              </Skeleton>
            );
          })}
        </LimeCheckboxGroup>
      </div>
      <Divider />
    </>
  );
};
